import { useState, useEffect } from 'react';
import type { Question } from '@omq/types';
import { Placeholder } from '@omq/types';
import { ServerError, useBackendContext, useConfigContext } from '@omq/shared';

import { QuestionListItem } from './question-list-item';
import { ContactAPI } from '../../api/contact';

import './question-list.less';

/**
 * Type for component properties.
 */
type QuestionListProps = {
  questions: Array<Question & { isLoading?: boolean }>;
  headline: string;
  selectedQuestionId: number | null;
  onQuestionSelect: (question: Question | null) => void;
  renderEmptyList: () => JSX.Element | string;
  placeholder: Placeholder;
  onError: (error: ServerError) => void;
};

/**
 * QuestionList component. Renders a list of questions.
 *
 * @param {QuestionListProps} props - Component properties
 *
 * @author Florian Walch
 * @since 9.3
 *
 * @returns {Node}
 */
export function QuestionList({
  questions,
  headline,
  selectedQuestionId,
  onQuestionSelect,
  renderEmptyList,
  placeholder,
  onError,
}: QuestionListProps): JSX.Element {
  const [selectedQuestion, setSelectedQuestion] = useState<
    (Question & { isLoading?: boolean }) | null
  >(null);

  const backend = useBackendContext();
  const config = useConfigContext();

  // loads the selected question if not already there
  useEffect(() => {
    // reset selected question if id is null
    if (selectedQuestionId == null) {
      setSelectedQuestion(null);
      return;
    }

    // find selected question
    const currentQuestion = questions.find((item) => item.id === selectedQuestionId);

    // Lazy load question content on click if answers have not been loaded yet
    if (currentQuestion && !currentQuestion.answers) {
      // set question is loading state to true
      setSelectedQuestion({ ...currentQuestion, isLoading: true });

      ContactAPI.question(backend, selectedQuestionId, placeholder)
        .then(async (result) => {
          // Set answers to the question
          setSelectedQuestion({ ...result, isLoading: false });

          onError(null);
        })
        .catch((error) => {
          console.error('Error loading question', selectedQuestionId);
          setSelectedQuestion({ ...currentQuestion, isLoading: false });
          onError(error);
        });
    }
    // sets the selected question
    else {
      setSelectedQuestion(currentQuestion);
    }
  }, [selectedQuestionId]);

  // screen reader announcement for found questions - results appear
  // dynamically while the user types into the surrounding form, a polite
  // live region is the only way screen reader users notice them
  const liveRegionText =
    questions.length > 0 ? config.loc('aria_questions_found', String(questions.length)) : '';

  // visually hidden live region - rendered as first child in both branches
  // so the same DOM node persists, a live region only announces changes
  // when it already exists in the DOM before the text appears
  const liveRegion = (
    <div
      className={config.generateClassName('question-list__live-region')}
      role="status"
      aria-live="polite">
      {liveRegionText}
    </div>
  );

  // if list is empty
  if (questions.length === 0) {
    return (
      <div className={config.generateClassName('question-list')}>
        {liveRegion}
        <div id="emptyListElement" className={config.generateClassName('question-list__empty')}>
          {renderEmptyList()}
        </div>
      </div>
    );
  }

  let className = config.generateClassName('question-list');
  if (selectedQuestion != null) {
    className += ` ${config.generateClassName('question-list--has-selection')}`;
  }

  // render list
  return (
    <div className={className}>
      {liveRegion}
      <div className={config.generateClassName('question-list__header')}>{headline}</div>
      {questions.map((question) => (
        <QuestionListItem
          key={question.id}
          question={question.id === selectedQuestion?.id ? selectedQuestion : question}
          isActive={selectedQuestion?.id === question.id}
          onSelect={onQuestionSelect}
        />
      ))}
    </div>
  );
}
